import React, { useContext, useState } from 'react'
import { toast } from 'react-toastify'

import { Button, Dialog, Icon, Popover } from '@clientbase/clientbase-library'
import { Box, useMediaQuery } from '@mui/material'
import { DataService } from 'api/DataService'
import copy from 'copy-to-clipboard'
import { mapChargeToDuplicateFormValues } from 'templates/Customers/Modals/BodyModalNewChargeOrRecurrence/BodyModalNewChargeOrRecurrence.edit.utils'
import InvoiceIcon from 'templates/Invoice/components/InvoiceIcon'

import Can from 'components/Can'
import ModalLinkNfseToBilling from 'components/Modal/ModalChargeDetail/components/NfseManualLink'
import { ModalChargeDetailContext } from 'components/Modal/ModalChargeDetail/context'
import {
  getStatus as getStatusEnum,
  openInvoice,
  sendToWhatsApp
} from 'components/Modal/ModalChargeDetail/ModalChargeDetail.utils'
import ModalContaAzulDetail from 'components/Modal/ModalContaAzulDetail'
import ContaAzulSection from 'components/Modal/ModalDetailsExpenses/components/Sessions/ContaAzulSection'
import ModalEmitNfseForCharge, {
  buildNfseChargeFromInvoice,
  isNfseEmittableStatus
} from 'components/Modal/ModalEmitNfseForCharge'

import { getCancellationLetterUrl, getInvoiceUrl } from 'utils/domain-urls'
import { toastProps } from 'utils/types/toast-props'

import { theme } from 'styles/theme'

import { S } from './'

const Actions: React.FC = () => {
  const {
    invoice,
    anchorEl,
    updateAnchorEl,
    updateModalNfDetails,
    scrollToNotifications,
    updateReload,
    onDuplicate
  } = useContext(ModalChargeDetailContext)
  const isMobile = useMediaQuery('(max-width:600px)')

  const [nfseLinkPickerOpen, setNfseLinkPickerOpen] = useState(false)
  const [nfseUnlinkOpen, setNfseUnlinkOpen] = useState(false)
  const [nfseUnlinking, setNfseUnlinking] = useState(false)
  const [emitNfOpen, setEmitNfOpen] = useState(false)
  const [contaAzulOpen, setContaAzulOpen] = useState(false)

  const handleOpenContaAzul = () => {
    updateAnchorEl(null)
    setContaAzulOpen(true)
  }

  // Vínculo manual de nota fiscal avulsa só existe quando a billing NÃO tem
  // automação de NFSe (nfse_policy no_nfse/on_demand) — ver Nfse::LinkBilling.
  const nfseManual = invoice?.nfseAutomationActive === false
  const nfseLinked = invoice?.linkedNfse
  const canLinkNfse = invoice?.canLinkNfse

  const canEmitNfse = isNfseEmittableStatus(invoice?.status)

  const reloadCharge = () => updateReload((prev) => !prev)

  const handleOpenNfseLinkPicker = () => {
    updateAnchorEl(null)
    setNfseLinkPickerOpen(true)
  }

  const handleOpenEmitNf = () => {
    updateAnchorEl(null)
    setEmitNfOpen(true)
  }

  const pinnedCharge = buildNfseChargeFromInvoice(invoice)

  const handleOpenNfseUnlink = () => {
    updateAnchorEl(null)
    setNfseUnlinkOpen(true)
  }

  const handleUnlinkNfse = async () => {
    setNfseUnlinking(true)
    const id = toast.loading('Removendo vínculo da nota fiscal...')

    const response = await DataService({
      type: 'DELETE',
      url: `/v1/billings/${invoice?.uuid}/unlink_nfse/${nfseLinked?.uuid}`
    })

    const errorMessage =
      response.error && (response.detail_error || response.message)

    toast.update(id, {
      render: response.error ? errorMessage : response.data.message,
      type: response.error ? 'error' : 'success',
      ...toastProps
    })

    setNfseUnlinking(false)
    setNfseUnlinkOpen(false)
    if (!response.error) reloadCharge()
  }

  const handleOpenInvoice = () => {
    openInvoice(invoice?.uuid)
  }

  const handleOpenNfDetails = () => {
    updateModalNfDetails(true)
  }

  function handleResendNotification() {
    scrollToNotifications()
  }

  function copyLinkInvoice(uuid: string) {
    copy(getInvoiceUrl(uuid))
    toast.success('Link copiado com sucesso!')
    updateAnchorEl(null)
  }

  function handleOpenCancellationLetter() {
    window.open(getCancellationLetterUrl(invoice?.uuid), '_blank')
    updateAnchorEl(null)
  }

  // Quem abre (e fecha) o modal de nova cobrança é o próprio ModalChargeDetail:
  // depender da página pai só funcionava quando o detalhe era aberto de dentro
  // de /cobrancas.
  const handleDuplicate = () => {
    if (!invoice) return
    updateAnchorEl(null)
    onDuplicate?.(mapChargeToDuplicateFormValues(invoice))
  }

  const isCancelledStatus =
    invoice?.status === 'cancelling' || invoice?.status === 'cancelled'
  const hasReversedPayment = invoice?.payments?.some(
    (payment) => payment.status === 'reversed'
  )
  const showCancellationLetter = isCancelledStatus && hasReversedPayment

  const handleToggleAnchor = (event?: React.MouseEvent<HTMLElement> | null) => {
    updateAnchorEl(event ? event.currentTarget : null)
  }

  const titleViewed =
    getStatusEnum(invoice?.status)?.label === 'Paga'
      ? 'Visualizar comprovante'
      : 'Visualizar fatura'

  return (
    <S.Wrapper>
      {invoice?.nfse_uuid && (
        <Button
          sx={{
            gap: '8px',
            [theme.breakpoints.down(450)]: {
              width: '100%'
            }
          }}
          onClick={() => handleOpenNfDetails()}
        >
          <S.NfButtonContent>
            <S.NfIconWrapper>
              <InvoiceIcon width={'20px'} color={'white'} />
            </S.NfIconWrapper>
            <Box mb={'8px'}>
              <p>Visualizar NF</p>
            </Box>
          </S.NfButtonContent>
        </Button>
      )}

      <Button
        sx={{
          [theme.breakpoints.down(450)]: {
            width: '100%'
          }
        }}
        onClick={handleResendNotification}
        fullWidth={isMobile}
      >
        Reenviar notificação
      </Button>

      {getStatusEnum(invoice?.status)?.label !== 'Cancelada' ? (
        <Button
          sx={{
            [theme.breakpoints.down(450)]: {
              width: '100%'
            }
          }}
          startIcon="receipt"
          onClick={handleOpenInvoice}
        >
          {titleViewed}
        </Button>
      ) : null}

      {/* O gatilho "Mais opções" subiu para a barra de título do modal (ver
          ModalChargeDetailBody); o Popover fica aqui porque é quem conhece os
          itens do menu, e o anchorEl vem do contexto. */}
      <Popover
        anchorEl={anchorEl}
        anchorOrigin={{
          horizontal: 'center',
          vertical: 'bottom'
        }}
        keepMounted={false}
        onClose={() => handleToggleAnchor(null)}
        open={Boolean(anchorEl)}
        transformOrigin={{
          horizontal: 'center',
          vertical: 'top'
        }}
      >
        <S.MenuItemsWrapper>
          {invoice?.contaAzulInfo && (
            <S.MenuItem onClick={handleOpenContaAzul}>
              <Icon icon="link" />
              Conta Azul
            </S.MenuItem>
          )}
          {getStatusEnum(invoice?.status)?.label !== 'Cancelada' && (
            <>
              <S.MenuItem onClick={() => copyLinkInvoice(invoice?.uuid)}>
                <Icon icon="contentCopy" />
                {getStatusEnum(invoice?.status)?.label !== 'Paga'
                  ? 'Copiar link fatura'
                  : 'Copiar link do comprovante'}
              </S.MenuItem>
              <S.MenuItem onClick={() => sendToWhatsApp(invoice)}>
                <Icon sx={{ transform: ' scaleX(-1)' }} icon={'reply'} />
                Enviar via WhatsApp
              </S.MenuItem>
            </>
          )}
          {showCancellationLetter && (
            <S.MenuItem onClick={handleOpenCancellationLetter}>
              <Icon icon="description" />
              Emitir carta de cancelamento
            </S.MenuItem>
          )}
          <Can permission="billings:manage">
            <S.MenuItem onClick={handleDuplicate}>
              <Icon icon="add" />
              Duplicar
            </S.MenuItem>
          </Can>
          {nfseManual && nfseLinked && (
            <Can permission="nfses:manage">
              <S.MenuItem onClick={handleOpenNfseUnlink}>
                <Icon icon="cancel" />
                Desvincular Nota Fiscal
              </S.MenuItem>
            </Can>
          )}
          {nfseManual && !nfseLinked && canLinkNfse && (
            <Can permission="nfses:manage">
              <S.MenuItem onClick={handleOpenNfseLinkPicker}>
                <Icon icon="link" />
                Vincular Nota Fiscal
              </S.MenuItem>
            </Can>
          )}
          {nfseManual && !nfseLinked && canLinkNfse && canEmitNfse && (
            <Can permission="nfses:manage">
              <S.MenuItem onClick={handleOpenEmitNf}>
                <Box
                  sx={{
                    width: 24,
                    display: 'inline-flex',
                    justifyContent: 'center',
                    alignItems: 'center'
                  }}
                >
                  <InvoiceIcon width={20} color="#3F403F" />
                </Box>
                Emitir Nota Fiscal
              </S.MenuItem>
            </Can>
          )}
        </S.MenuItemsWrapper>
      </Popover>

      {nfseManual && (
        <ModalLinkNfseToBilling
          billingUuid={invoice?.uuid as string}
          billingAmount={invoice?.amountBilled}
          open={nfseLinkPickerOpen}
          onClose={() => setNfseLinkPickerOpen(false)}
          onSuccess={reloadCharge}
        />
      )}

      {nfseManual &&
        !nfseLinked &&
        canLinkNfse &&
        canEmitNfse &&
        pinnedCharge && (
          <ModalEmitNfseForCharge
            open={emitNfOpen}
            onClose={() => setEmitNfOpen(false)}
            onEmitted={reloadCharge}
            charge={pinnedCharge}
            initialAmount={invoice?.amountBilled}
          />
        )}

      {nfseManual && (
        <Dialog
          icon="info"
          title="DESVINCULAR NOTA FISCAL"
          description="Confirma a remoção do vínculo desta nota fiscal com a cobrança? A nota não será cancelada."
          setIsOpenDialog={() => setNfseUnlinkOpen(false)}
          isOpenDialog={nfseUnlinkOpen}
          cancelButton
        >
          <Button fullWidth disabled={nfseUnlinking} onClick={handleUnlinkNfse}>
            {nfseUnlinking ? 'Removendo...' : 'Desvincular'}
          </Button>
        </Dialog>
      )}

      {invoice?.contaAzulInfo && (
        <ModalContaAzulDetail
          open={contaAzulOpen}
          onClose={() => setContaAzulOpen(false)}
        >
          <ContaAzulSection
            info={invoice.contaAzulInfo}
            onRefresh={reloadCharge}
          />
        </ModalContaAzulDetail>
      )}
    </S.Wrapper>
  )
}

export default Actions
